import type { VisibleCardViewV1 } from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  IDLE_ALPHA_CARD_DRAG_STATE,
  type AlphaCardDragState,
  type AlphaPlacementAction,
} from "./alpha-drag-types";

const TOUCH_UPWARD_THRESHOLD = 12;
const MOUSE_DRAG_THRESHOLD = 7;

interface DragCandidate {
  readonly card: VisibleCardViewV1;
  readonly pointerId: number;
  readonly pointerType: string;
  readonly originX: number;
  readonly originY: number;
  readonly trigger: HTMLButtonElement;
  readonly actionByLane: ReadonlyMap<
    AlphaCardDragState["legalLaneIds"][number],
    AlphaPlacementAction
  >;
  readonly originRect: Readonly<{ x: number; y: number; width: number; height: number }>;
  previousX: number;
  previousY: number;
  previousTimestamp: number;
  dragging: boolean;
}

export function getAlphaDragIntent({
  pointerType,
  horizontalDistance,
  verticalDistance,
}: {
  readonly pointerType: string;
  readonly horizontalDistance: number;
  readonly verticalDistance: number;
}): "drag" | "scroll" | "pending" {
  const absoluteHorizontal = Math.abs(horizontalDistance);
  const absoluteVertical = Math.abs(verticalDistance);
  if (absoluteHorizontal > MOUSE_DRAG_THRESHOLD && absoluteHorizontal > absoluteVertical) {
    return "scroll";
  }
  if (pointerType === "mouse") {
    return Math.hypot(horizontalDistance, verticalDistance) >= MOUSE_DRAG_THRESHOLD
      ? "drag"
      : "pending";
  }
  return verticalDistance <= -TOUCH_UPWARD_THRESHOLD && absoluteVertical > absoluteHorizontal
    ? "drag"
    : "pending";
}

export function useAlphaCardDrag({
  getLaneAtPoint,
  onDrop,
  onCancel,
}: {
  readonly getLaneAtPoint: (
    clientX: number,
    clientY: number,
  ) => AlphaCardDragState["hoveredLaneId"];
  readonly onDrop: (action: AlphaPlacementAction) => void;
  readonly onCancel: (reason: "invalid" | "cancelled") => void;
}) {
  const [state, setState] = useState<AlphaCardDragState>(IDLE_ALPHA_CARD_DRAG_STATE);
  const [draggedCard, setDraggedCard] = useState<VisibleCardViewV1 | null>(null);
  const stateRef = useRef<AlphaCardDragState>(IDLE_ALPHA_CARD_DRAG_STATE);
  const candidateRef = useRef<DragCandidate | null>(null);
  const callbacksRef = useRef({ getLaneAtPoint, onDrop, onCancel });
  const suppressClickCardIdRef = useRef<string | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    callbacksRef.current = { getLaneAtPoint, onDrop, onCancel };
  }, [getLaneAtPoint, onCancel, onDrop]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const clearAnimationFrame = useCallback(() => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const clearDrag = useCallback(
    (status: "idle" | "cancelled" = "idle") => {
      const candidate = candidateRef.current;
      if (candidate?.trigger.hasPointerCapture(candidate.pointerId)) {
        candidate.trigger.releasePointerCapture(candidate.pointerId);
      }
      candidateRef.current = null;
      clearAnimationFrame();
      setState(status === "idle" ? IDLE_ALPHA_CARD_DRAG_STATE : { ...stateRef.current, status });
      if (status === "idle") setDraggedCard(null);
    },
    [clearAnimationFrame],
  );

  const settle = useCallback(
    (nextState: AlphaCardDragState) => {
      clearSettleTimer();
      clearAnimationFrame();
      candidateRef.current = null;
      setState(nextState);
      settleTimerRef.current = window.setTimeout(() => {
        setState(IDLE_ALPHA_CARD_DRAG_STATE);
        setDraggedCard(null);
        settleTimerRef.current = null;
      }, 190);
    },
    [clearAnimationFrame, clearSettleTimer],
  );

  const cancelDrag = useCallback(
    (reason: "invalid" | "cancelled" = "cancelled") => {
      const candidate = candidateRef.current;
      if (candidate === null && stateRef.current.status === "idle") return;
      if (candidate?.dragging) suppressClickCardIdRef.current = candidate.card.cardInstanceId;
      clearSettleTimer();
      const cancelledState = {
        ...stateRef.current,
        status: "cancelled" as const,
      };
      clearDrag("cancelled");
      callbacksRef.current.onCancel(reason);
      settle(cancelledState);
    },
    [clearDrag, clearSettleTimer, settle],
  );

  const scheduleMove = useCallback(
    (nextState: AlphaCardDragState) => {
      clearAnimationFrame();
      rafRef.current = window.requestAnimationFrame(() => {
        setState(nextState);
        rafRef.current = null;
      });
    },
    [clearAnimationFrame],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || event.pointerId !== candidate.pointerId) return;
      const horizontalDistance = event.clientX - candidate.originX;
      const verticalDistance = event.clientY - candidate.originY;
      if (!candidate.dragging) {
        const intent = getAlphaDragIntent({
          pointerType: candidate.pointerType,
          horizontalDistance,
          verticalDistance,
        });
        if (intent === "scroll") {
          candidateRef.current = null;
          setState(IDLE_ALPHA_CARD_DRAG_STATE);
          setDraggedCard(null);
          return;
        }
        if (intent !== "drag") return;
        candidate.dragging = true;
        // Touch pointers need capture once their upward drag intent is confirmed. Mouse drags are
        // already followed by the window listener below, which keeps the desktop path lightweight
        // while the hand and target lanes reposition.
        if (candidate.pointerType !== "mouse") {
          candidate.trigger.setPointerCapture(candidate.pointerId);
        }
      }
      event.preventDefault();
      const timestamp = event.timeStamp;
      const elapsed = Math.max(1, timestamp - candidate.previousTimestamp);
      const velocityX = (event.clientX - candidate.previousX) / elapsed;
      const velocityY = (event.clientY - candidate.previousY) / elapsed;
      const hoveredLaneId = callbacksRef.current.getLaneAtPoint(event.clientX, event.clientY);
      scheduleMove({
        status: "dragging",
        pointerId: candidate.pointerId,
        cardInstanceId: candidate.card.cardInstanceId,
        pointerType: candidate.pointerType,
        originX: candidate.originX,
        originY: candidate.originY,
        currentX: event.clientX,
        currentY: event.clientY,
        previousX: candidate.previousX,
        previousY: candidate.previousY,
        previousTimestamp: candidate.previousTimestamp,
        velocityX,
        velocityY,
        offsetX: horizontalDistance,
        offsetY: verticalDistance,
        originRect: candidate.originRect,
        visualPhase: "travel",
        dropDestinationRect: null,
        hoveredLaneId,
        legalLaneIds: [...candidate.actionByLane.keys()],
        actionByLane: candidate.actionByLane,
      });
      candidate.previousX = event.clientX;
      candidate.previousY = event.clientY;
      candidate.previousTimestamp = timestamp;
    },
    [scheduleMove],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || event.pointerId !== candidate.pointerId) return;
      if (!candidate.dragging) {
        candidateRef.current = null;
        setState(IDLE_ALPHA_CARD_DRAG_STATE);
        setDraggedCard(null);
        return;
      }
      event.preventDefault();
      suppressClickCardIdRef.current = candidate.card.cardInstanceId;
      const hoveredLaneId = callbacksRef.current.getLaneAtPoint(event.clientX, event.clientY);
      const action = hoveredLaneId === null ? undefined : candidate.actionByLane.get(hoveredLaneId);
      const nextState: AlphaCardDragState = {
        status: action === undefined ? "cancelled" : "dropping",
        pointerId: candidate.pointerId,
        cardInstanceId: candidate.card.cardInstanceId,
        pointerType: candidate.pointerType,
        originX: candidate.originX,
        originY: candidate.originY,
        currentX: event.clientX,
        currentY: event.clientY,
        previousX: candidate.previousX,
        previousY: candidate.previousY,
        previousTimestamp: candidate.previousTimestamp,
        velocityX: stateRef.current.velocityX,
        velocityY: stateRef.current.velocityY,
        offsetX: event.clientX - candidate.originX,
        offsetY: event.clientY - candidate.originY,
        originRect: candidate.originRect,
        visualPhase: action === undefined ? "return" : "snap",
        dropDestinationRect: null,
        hoveredLaneId,
        legalLaneIds: [...candidate.actionByLane.keys()],
        actionByLane: candidate.actionByLane,
      };
      candidateRef.current = null;
      if (candidate.trigger.hasPointerCapture(candidate.pointerId)) {
        candidate.trigger.releasePointerCapture(candidate.pointerId);
      }
      if (action === undefined) callbacksRef.current.onCancel("invalid");
      else callbacksRef.current.onDrop(action);
      settle(nextState);
    },
    [settle],
  );

  const handlePointerCancel = useCallback(
    (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate?.pointerId === event.pointerId) cancelDrag("cancelled");
    },
    [cancelDrag],
  );

  const handleLostPointerCapture = useCallback(
    (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (
        candidate?.pointerId === event.pointerId &&
        candidate.pointerType !== "mouse" &&
        candidate.dragging
      ) {
        cancelDrag("cancelled");
      }
    },
    [cancelDrag],
  );

  useEffect(() => {
    window.addEventListener("pointermove", handlePointerMove, { passive: false });
    window.addEventListener("pointerup", handlePointerUp, { passive: false });
    window.addEventListener("pointercancel", handlePointerCancel);
    window.addEventListener("lostpointercapture", handleLostPointerCapture);
    const onBlur = () => cancelDrag("cancelled");
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") cancelDrag("cancelled");
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancelDrag("cancelled");
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("keydown", onKeyDown);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
      window.removeEventListener("lostpointercapture", handleLostPointerCapture);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [
    cancelDrag,
    handleLostPointerCapture,
    handlePointerCancel,
    handlePointerMove,
    handlePointerUp,
  ]);

  useEffect(
    () => () => {
      clearSettleTimer();
      clearAnimationFrame();
    },
    [clearAnimationFrame, clearSettleTimer],
  );

  const beginPress = useCallback(
    (
      card: VisibleCardViewV1,
      actionByLane: ReadonlyMap<AlphaCardDragState["legalLaneIds"][number], AlphaPlacementAction>,
      event: ReactPointerEvent<HTMLButtonElement>,
    ) => {
      if (event.button !== 0 || actionByLane.size === 0) return;
      clearSettleTimer();
      candidateRef.current = {
        card,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        originX: event.clientX,
        originY: event.clientY,
        trigger: event.currentTarget,
        actionByLane,
        originRect: {
          x: event.currentTarget.getBoundingClientRect().x,
          y: event.currentTarget.getBoundingClientRect().y,
          width: event.currentTarget.getBoundingClientRect().width,
          height: event.currentTarget.getBoundingClientRect().height,
        },
        previousX: event.clientX,
        previousY: event.clientY,
        previousTimestamp: event.timeStamp,
        dragging: false,
      };
      setDraggedCard(card);
      setState({
        status: "pressed",
        pointerId: event.pointerId,
        cardInstanceId: card.cardInstanceId,
        pointerType: event.pointerType,
        originX: event.clientX,
        originY: event.clientY,
        currentX: event.clientX,
        currentY: event.clientY,
        previousX: event.clientX,
        previousY: event.clientY,
        previousTimestamp: event.timeStamp,
        velocityX: 0,
        velocityY: 0,
        offsetX: 0,
        offsetY: 0,
        originRect: {
          x: event.currentTarget.getBoundingClientRect().x,
          y: event.currentTarget.getBoundingClientRect().y,
          width: event.currentTarget.getBoundingClientRect().width,
          height: event.currentTarget.getBoundingClientRect().height,
        },
        visualPhase: "lift",
        dropDestinationRect: null,
        hoveredLaneId: null,
        legalLaneIds: [...actionByLane.keys()],
        actionByLane,
      });
    },
    [clearSettleTimer],
  );

  const consumeSuppressedClick = useCallback((cardInstanceId: string) => {
    if (suppressClickCardIdRef.current !== cardInstanceId) return false;
    suppressClickCardIdRef.current = null;
    return true;
  }, []);

  return {
    state,
    draggedCard,
    isActive:
      state.status === "pressed" || state.status === "dragging" || state.status === "dropping",
    beginPress,
    cancelDrag,
    consumeSuppressedClick,
  };
}
